import * as React from "react";
import { useCallback, useState } from "react";
import { XIcon } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import { updateReportStrategyPolicyVersion } from "../../../services/reportsApi";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { StrategyPolicyViewBody } from "./StrategyPolicyViewBody";
import { PolicyVersionTitleEditor } from "./PolicyVersionTitleEditor";
import { policyVersionDisplayTitle } from "./strategyDetailUtils";

export type StrategyPolicyViewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  policyTree: unknown;
  policyVersions?: ReportStrategyPolicyVersion[];
  /** Active version for BE policy_kinds chips. */
  policyVersion?: ReportStrategyPolicyVersion | null;
  strategyId?: string | null;
  /** Called after a successful title PATCH (refresh versions list). */
  onTitleSaved?: (version: ReportStrategyPolicyVersion) => void | Promise<void>;
};

export function StrategyPolicyViewDialog({
  open,
  onOpenChange,
  title,
  policyTree,
  policyVersions = [],
  policyVersion = null,
  strategyId = null,
  onTitleSaved,
}: StrategyPolicyViewDialogProps): React.ReactElement {
  const [localVersion, setLocalVersion] = useState<ReportStrategyPolicyVersion | null>(null);
  const [savingTitle, setSavingTitle] = useState(false);

  const activeVersion = localVersion?.id === policyVersion?.id ? localVersion : policyVersion;
  const displayTitle =
    (activeVersion ? policyVersionDisplayTitle(activeVersion) : "") || title.trim() || "version";
  const canRename = Boolean(strategyId?.trim() && activeVersion?.id);

  React.useEffect(() => {
    if (!open) setLocalVersion(null);
  }, [open]);

  const saveTitle = useCallback(
    async (nextTitle: string) => {
      const sid = strategyId?.trim();
      if (!sid || !activeVersion?.id || savingTitle) return;
      setSavingTitle(true);
      try {
        const updated = await updateReportStrategyPolicyVersion(sid, activeVersion.id, {
          title: nextTitle,
        });
        setLocalVersion(updated);
        toast.success(nextTitle.trim() ? "Policy name saved" : "Policy name cleared");
        await onTitleSaved?.(updated);
      } catch (err) {
        presentMutationError(err, "Could not save policy name");
      } finally {
        setSavingTitle(false);
      }
    },
    [activeVersion?.id, onTitleSaved, savingTitle, strategyId]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        data-policy-view-dialog=""
        className={cn("flex max-h-[min(90vh,48rem)] flex-col overflow-hidden sm:max-w-6xl")}
      >
        <DialogClose asChild>
          <button
            type="button"
            data-policy-view-dismiss=""
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex size-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>

        <DialogHeader className="shrink-0 gap-2">
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            Policy
          </DialogTitle>
          {canRename && activeVersion ? (
            <PolicyVersionTitleEditor
              version={activeVersion}
              saving={savingTitle}
              onSaveTitle={saveTitle}
            />
          ) : (
            <p className="m-0 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
              {displayTitle}
            </p>
          )}
          <DialogDescription className="font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-white">
            Execution rules and filter groups saved in this policy version.
          </DialogDescription>
        </DialogHeader>

        <StrategyPolicyViewBody
          title={displayTitle}
          policyTree={policyTree}
          policyVersions={policyVersions}
          policyVersion={activeVersion}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 pt-1 pb-2 [scrollbar-gutter:stable]"
        />

        <DialogFooter className="shrink-0 sm:justify-end">
          <DialogClose asChild>
            <button type="button" data-policy-view-close="">
              Close
            </button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
